// Análise das conversas de WhatsApp pela IA (#6313).
//
// Todo dia, a IA lê as conversas de cada WhatsApp conectado ao Inbox (o da
// Júlia primeiro) e devolve, por conversa, a chance de fechamento, a etapa e o
// próximo passo; e, no fim, um relatório do dia com resumo, feedback e
// insights. Este arquivo é a parte PURA: o que entra no pedido à IA, como a
// resposta é conferida e as contas que NÃO são da IA.
//
// ⚠️ Números objetivos (tempo de resposta, cliente sem resposta, volume) são
// calculados aqui, nunca pedidos ao modelo. Modelo erra conta e inventa número
// com a mesma confiança com que acerta — e é exatamente o número que a gestão
// vai cobrar.

// ─── Dia ────────────────────────────────────────────────────────────────────

/** Brasília sem horário de verão desde 2019: o fuso é fixo. */
const FUSO = '-03:00';

/** Início e fim (exclusivo) de um dia de Brasília, em ISO. */
export function janelaDoDia(dia: string): { inicio: string; fim: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) throw new Error(`Dia inválido: ${dia}`);
  const inicio = new Date(`${dia}T00:00:00${FUSO}`);
  const fim = new Date(inicio.getTime() + 24 * 60 * 60 * 1000);
  return { inicio: inicio.toISOString(), fim: fim.toISOString() };
}

/** "Hoje" em Brasília, no formato YYYY-MM-DD. */
export function hojeEmBrasilia(agora: Date = new Date()): string {
  return new Date(agora.getTime() - 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** Hora (0–23) de um instante, em Brasília. */
function horaEmBrasilia(iso: string): number {
  return new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000).getUTCHours();
}

// ─── Mensagens ──────────────────────────────────────────────────────────────

export interface MensagemSimples {
  id: string;
  direction: 'inbound' | 'outbound';
  type: string;
  body: string | null;
  provider_timestamp: string;
  sender_user_id?: string | null;
  anexos?: Array<{ mime_type: string | null; file_name: string | null; transcricao?: string | null }>;
}

/** Aviso de sistema sem texto (reação, apagada, troca de chave) não é conversa. */
export function ehConteudo(m: MensagemSimples): boolean {
  if (m.type === 'system') return !!m.body?.trim();
  return true;
}

const LIMITE_TEXTO = 800;

function cortar(texto: string, limite: number): string {
  const t = texto.replace(/\s+/g, ' ').trim();
  return t.length > limite ? `${t.slice(0, limite - 1)}…` : t;
}

/** O que uma mensagem diz, em uma linha. Mídia vira etiqueta; áudio vira a transcrição. */
export function textoDaMensagem(m: MensagemSimples): string {
  const partes: string[] = [];
  for (const a of m.anexos ?? []) {
    const mime = a.mime_type ?? '';
    if (mime.startsWith('audio/')) {
      partes.push(a.transcricao?.trim() ? `(áudio) ${cortar(a.transcricao, LIMITE_TEXTO)}` : '(áudio não transcrito)');
    } else if (mime.startsWith('image/')) {
      partes.push(m.type === 'sticker' ? '(figurinha)' : '(imagem)');
    } else if (mime.startsWith('video/')) {
      partes.push('(vídeo)');
    } else {
      partes.push(`(documento${a.file_name ? `: ${cortar(a.file_name, 80)}` : ''})`);
    }
  }
  if (!m.anexos?.length && m.type === 'audio') partes.push('(áudio não transcrito)');
  if (m.body?.trim()) partes.push(cortar(m.body, LIMITE_TEXTO));
  return partes.join(' ') || '(mensagem sem texto)';
}

function carimbo(iso: string): string {
  const d = new Date(new Date(iso).getTime() - 3 * 60 * 60 * 1000);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
}

/** A conversa como texto corrido, do jeito que o modelo lê. */
export function transcreverConversa(msgs: MensagemSimples[], nomeAtendente: string): string {
  return msgs
    .filter(ehConteudo)
    .map((m) => `[${carimbo(m.provider_timestamp)}] ${m.direction === 'inbound' ? 'Cliente' : nomeAtendente}: ${textoDaMensagem(m)}`)
    .join('\n');
}

// ─── Métricas objetivas ─────────────────────────────────────────────────────

/** Turno do cliente que começa fora disto não entra no tempo de resposta. */
export const HORARIO_COMERCIAL = { inicio: 8, fim: 18 };

export interface MetricasConversa {
  recebidas: number;
  enviadas: number;
  audios_recebidos: number;
  audios_enviados: number;
  /** Minutos entre o cliente começar a falar e a primeira resposta, por turno, no horário comercial. */
  tempos_resposta_min: number[];
  /** Turnos do cliente que terminaram o dia sem resposta. */
  turnos_sem_resposta: number;
  /** A última mensagem do dia é do cliente: ele ficou esperando. */
  cliente_sem_resposta: boolean;
}

/**
 * Conta o dia de uma conversa.
 *
 * `anterior` é a última mensagem ANTES do dia: se o cliente já vinha falando,
 * a primeira mensagem dele no dia não abre turno novo (é continuação).
 */
export function metricasDaConversa(doDia: MensagemSimples[], anterior?: MensagemSimples | null): MetricasConversa {
  const msgs = doDia.filter(ehConteudo).slice().sort((a, b) => a.provider_timestamp.localeCompare(b.provider_timestamp));
  const r: MetricasConversa = {
    recebidas: 0, enviadas: 0, audios_recebidos: 0, audios_enviados: 0,
    tempos_resposta_min: [], turnos_sem_resposta: 0, cliente_sem_resposta: false,
  };

  let turnoAberto: string | null = null; // timestamp do início do turno do cliente
  let turnoConta = false; // começou no horário comercial
  let ultimaDirecao = anterior && ehConteudo(anterior) ? anterior.direction : null;

  for (const m of msgs) {
    const audio = m.type === 'audio' || (m.anexos ?? []).some((a) => (a.mime_type ?? '').startsWith('audio/'));
    if (m.direction === 'inbound') {
      r.recebidas++;
      if (audio) r.audios_recebidos++;
      if (ultimaDirecao !== 'inbound' && turnoAberto === null) {
        turnoAberto = m.provider_timestamp;
        const h = horaEmBrasilia(m.provider_timestamp);
        turnoConta = h >= HORARIO_COMERCIAL.inicio && h < HORARIO_COMERCIAL.fim;
      }
    } else {
      r.enviadas++;
      if (audio) r.audios_enviados++;
      if (turnoAberto !== null) {
        if (turnoConta) {
          const min = (new Date(m.provider_timestamp).getTime() - new Date(turnoAberto).getTime()) / 60000;
          r.tempos_resposta_min.push(Math.max(0, Math.round(min * 10) / 10));
        }
        turnoAberto = null;
      }
    }
    ultimaDirecao = m.direction;
  }

  if (turnoAberto !== null) r.turnos_sem_resposta++;
  r.cliente_sem_resposta = msgs.length > 0 && msgs[msgs.length - 1].direction === 'inbound';
  return r;
}

function percentil(valores: number[], p: number): number | null {
  if (!valores.length) return null;
  const v = valores.slice().sort((a, b) => a - b);
  const i = Math.min(v.length - 1, Math.max(0, Math.ceil((p / 100) * v.length) - 1));
  return v[i];
}

// ─── Resposta da IA por conversa ────────────────────────────────────────────

export const ETAPAS = [
  'primeiro_contato',
  'qualificacao',
  'apresentacao',
  'simulacao_proposta',
  'objecoes_negociacao',
  'documentos_assinatura',
  'fechado',
  'perdido',
  'pos_venda_suporte',
] as const;
export type Etapa = typeof ETAPAS[number];

export const TEMPERATURAS = ['quente', 'morna', 'fria'] as const;
export type Temperatura = typeof TEMPERATURAS[number];

const SITUACOES_BANT = ['identificado', 'parcial', 'nao_identificado'] as const;
const GRAVIDADES = ['alta', 'media', 'baixa'] as const;

export interface AlertaPolitica {
  regra: string;
  trecho: string;
  explicacao: string;
  gravidade: typeof GRAVIDADES[number];
}

export interface AnaliseDaConversa {
  comercial: boolean;
  probabilidade: number | null;
  temperatura: Temperatura | null;
  etapa: Etapa;
  justificativa: string;
  resumo: string;
  proximo_passo: string;
  objecoes: Array<{ objecao: string; tratada: boolean; comentario: string }>;
  avaliacao: {
    spin: { nota: number; comentario: string };
    bant: { orcamento: string; autoridade: string; necessidade: string; prazo: string };
    objecoes: { nota: number; comentario: string };
    fechamento: { nota: number; comentario: string };
    clareza: { nota: number; comentario: string };
  };
  pontos_fortes: string[];
  melhorias: string[];
  alertas_politica: AlertaPolitica[];
}

const nota = { type: 'object', additionalProperties: false, required: ['nota', 'comentario'], properties: { nota: { type: 'integer' }, comentario: { type: 'string' } } };
const textos = { type: 'array', items: { type: 'string' } };
const alertas = {
  type: 'array',
  items: {
    type: 'object', additionalProperties: false, required: ['regra', 'trecho', 'explicacao', 'gravidade'],
    properties: {
      regra: { type: 'string' },
      trecho: { type: 'string' },
      explicacao: { type: 'string' },
      gravidade: { type: 'string', enum: [...GRAVIDADES] },
    },
  },
};

/** JSON Schema da resposta por conversa (modo estrito da OpenAI: tudo obrigatório). */
export const SCHEMA_CONVERSA = {
  type: 'object',
  additionalProperties: false,
  required: ['comercial', 'probabilidade', 'temperatura', 'etapa', 'justificativa', 'resumo', 'proximo_passo', 'objecoes', 'avaliacao', 'pontos_fortes', 'melhorias', 'alertas_politica'],
  properties: {
    comercial: { type: 'boolean' },
    probabilidade: { type: ['integer', 'null'] },
    temperatura: { type: ['string', 'null'], enum: [...TEMPERATURAS, null] },
    etapa: { type: 'string', enum: [...ETAPAS] },
    justificativa: { type: 'string' },
    resumo: { type: 'string' },
    proximo_passo: { type: 'string' },
    objecoes: {
      type: 'array',
      items: {
        type: 'object', additionalProperties: false, required: ['objecao', 'tratada', 'comentario'],
        properties: { objecao: { type: 'string' }, tratada: { type: 'boolean' }, comentario: { type: 'string' } },
      },
    },
    avaliacao: {
      type: 'object', additionalProperties: false,
      required: ['spin', 'bant', 'objecoes', 'fechamento', 'clareza'],
      properties: {
        spin: nota,
        bant: {
          type: 'object', additionalProperties: false, required: ['orcamento', 'autoridade', 'necessidade', 'prazo'],
          properties: {
            orcamento: { type: 'string', enum: [...SITUACOES_BANT] },
            autoridade: { type: 'string', enum: [...SITUACOES_BANT] },
            necessidade: { type: 'string', enum: [...SITUACOES_BANT] },
            prazo: { type: 'string', enum: [...SITUACOES_BANT] },
          },
        },
        objecoes: nota,
        fechamento: nota,
        clareza: nota,
      },
    },
    pontos_fortes: textos,
    melhorias: textos,
    alertas_politica: alertas,
  },
} as const;

const texto = (v: unknown, limite: number): string => (typeof v === 'string' ? cortar(v, limite) : '');
const lista = (v: unknown, max: number, limite = 300): string[] =>
  Array.isArray(v) ? v.map((x) => texto(x, limite)).filter(Boolean).slice(0, max) : [];
const inteiro = (v: unknown, min: number, max: number): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : null;
};
const deLista = <T extends string>(v: unknown, opcoes: readonly T[], padrao: T): T =>
  (opcoes as readonly string[]).includes(v as string) ? (v as T) : padrao;
const notaDe = (v: any) => ({ nota: inteiro(v?.nota, 0, 10) ?? 0, comentario: texto(v?.comentario, 300) });

/**
 * Confere e limpa o que voltou do modelo.
 *
 * O modo estrito já garante o formato; isto protege contra o resto: número
 * fora da faixa, lista enorme, alerta sem o trecho citado (alerta sem prova não
 * serve para conversar com ninguém) e probabilidade em conversa que não é venda.
 */
export function limparAnaliseConversa(bruto: any): AnaliseDaConversa {
  const comercial = bruto?.comercial !== false;
  const etapa = deLista(bruto?.etapa, ETAPAS, comercial ? 'primeiro_contato' : 'pos_venda_suporte');
  return {
    comercial,
    probabilidade: comercial ? inteiro(bruto?.probabilidade, 0, 100) : null,
    temperatura: comercial ? deLista(bruto?.temperatura, TEMPERATURAS, 'morna') : null,
    etapa,
    justificativa: texto(bruto?.justificativa, 500),
    resumo: texto(bruto?.resumo, 700),
    proximo_passo: texto(bruto?.proximo_passo, 300),
    objecoes: Array.isArray(bruto?.objecoes)
      ? bruto.objecoes.slice(0, 6).map((o: any) => ({ objecao: texto(o?.objecao, 150), tratada: !!o?.tratada, comentario: texto(o?.comentario, 300) })).filter((o: any) => o.objecao)
      : [],
    avaliacao: {
      spin: notaDe(bruto?.avaliacao?.spin),
      bant: {
        orcamento: deLista(bruto?.avaliacao?.bant?.orcamento, SITUACOES_BANT, 'nao_identificado'),
        autoridade: deLista(bruto?.avaliacao?.bant?.autoridade, SITUACOES_BANT, 'nao_identificado'),
        necessidade: deLista(bruto?.avaliacao?.bant?.necessidade, SITUACOES_BANT, 'nao_identificado'),
        prazo: deLista(bruto?.avaliacao?.bant?.prazo, SITUACOES_BANT, 'nao_identificado'),
      },
      objecoes: notaDe(bruto?.avaliacao?.objecoes),
      fechamento: notaDe(bruto?.avaliacao?.fechamento),
      clareza: notaDe(bruto?.avaliacao?.clareza),
    },
    pontos_fortes: lista(bruto?.pontos_fortes, 4),
    melhorias: lista(bruto?.melhorias, 4),
    alertas_politica: Array.isArray(bruto?.alertas_politica)
      ? bruto.alertas_politica
          .map((a: any) => ({
            regra: texto(a?.regra, 120),
            trecho: texto(a?.trecho, 300),
            explicacao: texto(a?.explicacao, 400),
            gravidade: deLista(a?.gravidade, GRAVIDADES, 'media'),
          }))
          .filter((a: AlertaPolitica) => a.regra && a.trecho)
          .slice(0, 5)
      : [],
  };
}

// ─── Política comercial: o que a IA confere ────────────────────────────────

/**
 * Promessas que não podem ser feitas ao cliente.
 *
 * Decidido pelo Felipe em 23/10/2026 que a IA aponta isso como ALERTA, com o
 * trecho da conversa. As regras saem do termo de adesão e do playbook do
 * Comercial Interno (#6272); se a política mudar, é aqui que se edita.
 */
export const REGRAS_POLITICA: string[] = [
  'Fidelidade: só dizer que "não tem fidelidade" (ou que pode cancelar quando quiser sem custo) se o plano da negociação for "Sem fidelidade". Com fidelidade, cancelar antes do prazo gera multa, e a fidelidade e a carência contam a partir do primeiro desconto.',
  'Desconto: não prometer desconto na tarifa maior que o do plano da negociação. Acima de 30% só existe com aprovação do gerente.',
  'Economia: não prometer o percentual de desconto sobre a conta inteira nem números de economia sem simulação. O desconto vale sobre a energia compensada; taxa mínima, iluminação pública e impostos continuam.',
  'Boleto único: não garantir que "vem tudo num boleto só". Depende de o cliente aceitar no Portal do Consumidor.',
  'Prazos: não prometer data de início do desconto. Depende da análise cadastral, da assinatura e do ciclo da Equatorial.',
];

// ─── Pedido à IA: conversa ──────────────────────────────────────────────────

export interface ContextoNegociacao {
  id: number;
  cliente_nome: string | null;
  status: string | null;
  fidelidade: string | null;
  desconto_tarifa: number | null; // fração: 0.3 = 30%
  desconto_bandeira: number | null;
  carencia: string | null;
}

export interface EntradaConversa {
  dia: string;
  nomeAtendente: string;
  contatoNome: string | null;
  negociacao: ContextoNegociacao | null;
  /** Mais de uma negociação com o número: o plano não é conferido. */
  negociacoesComNumero: number;
  analiseAnterior: { dia: string; probabilidade: number | null; resumo: string | null } | null;
  contexto: string;
  doDia: string;
}

const SISTEMA_CONVERSA = `Você é um coach de vendas sênior que avalia atendimentos comerciais por WhatsApp da Vigor Energy.

Sobre a Vigor: vende energia solar por assinatura (geração distribuída compartilhada) em Goiás. O cliente continua ligado à Equatorial, não instala nada, e recebe créditos das usinas da Vigor que viram desconto na tarifa de energia. O caminho da venda é: contato, entender a conta de luz e o consumo, simulação de economia, envio do termo de adesão, análise cadastral (crédito) e assinatura.

Avalie com metodologias comerciais comprovadas:
- SPIN Selling: o atendente fez perguntas de Situação, Problema, Implicação e Necessidade de solução, ou só empurrou oferta?
- BANT adaptado: Orçamento = conta de luz/consumo médio conhecido; Autoridade = fala com o titular ou com quem decide; Necessidade = dor de economia clara; Prazo = quando quer começar.
- Tratamento de objeções: ouviu, validou, respondeu com dado e confirmou que a objeção foi resolvida.
- Condução para o fechamento: terminou com próximo passo combinado (data, documento, simulação, assinatura) ou deixou a conversa morrer?
- Clareza e tom: mensagens claras, sem excesso, linguagem adequada.

Probabilidade de fechamento: estime de 0 a 100 a chance de este cliente assinar nas próximas semanas, com base no que foi dito (interesse, objeções resolvidas ou não, documentos enviados, silêncio do cliente). Seja calibrado: interesse vago sem conta enviada raramente passa de 30; termo enviado com cliente engajado pode passar de 70; cliente que sumiu cai.

Se a conversa não for de venda (suporte de fatura, cobrança, cliente já ativo, fornecedor, assunto pessoal), marque comercial=false, probabilidade=null, temperatura=null e etapa=pos_venda_suporte, e avalie só a qualidade do atendimento.

Regras de política comercial. Aponte em alertas_politica TODA promessa do atendente que contrarie estas regras ou o plano da negociação informado. Cite em "trecho" as palavras exatas da mensagem. Não aponte nada sem trecho que prove. Se o plano não foi informado, só aponte o que contraria as regras gerais.
${REGRAS_POLITICA.map((r) => `- ${r}`).join('\n')}

Responda em português do Brasil, direto e específico para ESTA conversa: nada de conselho genérico. Pontos fortes e melhorias: no máximo 3 cada, cada um com um exemplo do que foi ou deveria ter sido dito. O próximo passo é uma ação concreta para o atendente fazer amanhã.`;

function descreverPlano(n: ContextoNegociacao): string {
  const pct = (v: number | null) => (v == null ? 'não informado' : `${Math.round(v * 100)}%`);
  return [
    `Negociação #${n.id}${n.cliente_nome ? ` — ${n.cliente_nome}` : ''} (situação no CRM: ${n.status ?? 'não informada'})`,
    `Plano: fidelidade ${n.fidelidade || 'Sem fidelidade'}; desconto na tarifa ${pct(n.desconto_tarifa)}; desconto na bandeira ${pct(n.desconto_bandeira)}; carência ${n.carencia || 'não informada'}.`,
  ].join('\n');
}

export function pedidoConversa(e: EntradaConversa): { sistema: string; usuario: string } {
  const partes: string[] = [];
  partes.push(`Dia analisado: ${e.dia.split('-').reverse().join('/')}. Atendente: ${e.nomeAtendente}. Contato: ${e.contatoNome?.trim() || 'sem nome'}.`);
  if (e.negociacao) partes.push(descreverPlano(e.negociacao));
  else if (e.negociacoesComNumero > 1) partes.push(`O número do cliente está em ${e.negociacoesComNumero} negociações diferentes: o plano não foi informado.`);
  else partes.push('Nenhuma negociação no CRM com este número: o plano não foi informado.');
  if (e.analiseAnterior) {
    partes.push(`Análise anterior (${e.analiseAnterior.dia.split('-').reverse().join('/')}): probabilidade ${e.analiseAnterior.probabilidade ?? 'n/a'}%. ${e.analiseAnterior.resumo ?? ''}`);
  }
  partes.push(e.contexto ? `\nMensagens anteriores (só contexto, não avalie):\n${e.contexto}` : '\nSem mensagens anteriores.');
  partes.push(`\nMensagens do dia (avalie estas):\n${e.doDia}`);
  return { sistema: SISTEMA_CONVERSA, usuario: partes.join('\n') };
}

// ─── Relatório do dia ───────────────────────────────────────────────────────

/** O que fica gravado por conversa e alimenta o relatório. */
export interface LinhaAnalise {
  conversation_id: string;
  contato_nome: string | null;
  negociacao_id: number | null;
  comercial: boolean;
  probabilidade: number | null;
  probabilidade_anterior: number | null;
  temperatura: string | null;
  etapa: string | null;
  resumo: string | null;
  proximo_passo: string | null;
  cliente_sem_resposta: boolean;
  metricas: MetricasConversa;
  avaliacao: AnaliseDaConversa['avaliacao'] | Record<string, never>;
  melhorias: string[];
  pontos_fortes: string[];
  alertas_politica: AlertaPolitica[];
  objecoes: AnaliseDaConversa['objecoes'];
}

export interface MetricasDia {
  conversas: number;
  conversas_comerciais: number;
  mensagens_recebidas: number;
  mensagens_enviadas: number;
  audios_recebidos: number;
  audios_enviados: number;
  respostas_medidas: number;
  tempo_resposta_mediana_min: number | null;
  tempo_resposta_p90_min: number | null;
  clientes_sem_resposta: number;
  probabilidade_media: number | null;
  quentes: number;
  mornas: number;
  frias: number;
  alertas_politica: number;
}

export function agregarMetricas(linhas: LinhaAnalise[]): MetricasDia {
  const comerciais = linhas.filter((l) => l.comercial);
  const tempos = linhas.flatMap((l) => l.metricas.tempos_resposta_min);
  const probs = comerciais.map((l) => l.probabilidade).filter((p): p is number => p != null);
  const soma = (f: (l: LinhaAnalise) => number) => linhas.reduce((s, l) => s + f(l), 0);
  return {
    conversas: linhas.length,
    conversas_comerciais: comerciais.length,
    mensagens_recebidas: soma((l) => l.metricas.recebidas),
    mensagens_enviadas: soma((l) => l.metricas.enviadas),
    audios_recebidos: soma((l) => l.metricas.audios_recebidos),
    audios_enviados: soma((l) => l.metricas.audios_enviados),
    respostas_medidas: tempos.length,
    tempo_resposta_mediana_min: percentil(tempos, 50),
    tempo_resposta_p90_min: percentil(tempos, 90),
    clientes_sem_resposta: linhas.filter((l) => l.cliente_sem_resposta).length,
    probabilidade_media: probs.length ? Math.round(probs.reduce((a, b) => a + b, 0) / probs.length) : null,
    quentes: comerciais.filter((l) => l.temperatura === 'quente').length,
    mornas: comerciais.filter((l) => l.temperatura === 'morna').length,
    frias: comerciais.filter((l) => l.temperatura === 'fria').length,
    alertas_politica: soma((l) => l.alertas_politica.length),
  };
}

export interface Oportunidade {
  conversation_id: string;
  contato_nome: string | null;
  negociacao_id: number | null;
  probabilidade: number | null;
  probabilidade_anterior: number | null;
  proximo_passo: string | null;
  motivo: string;
}

/**
 * Quentes e em risco — calculado, não pedido à IA.
 *
 * Em risco = venda que ainda vale a pena (≥ 30%) e que hoje está escapando:
 * o cliente ficou sem resposta ou a chance caiu 20 pontos ou mais desde a
 * última análise.
 */
export function oportunidadesDoDia(linhas: LinhaAnalise[]): { quentes: Oportunidade[]; em_risco: Oportunidade[] } {
  const base = (l: LinhaAnalise, motivo: string): Oportunidade => ({
    conversation_id: l.conversation_id,
    contato_nome: l.contato_nome,
    negociacao_id: l.negociacao_id,
    probabilidade: l.probabilidade,
    probabilidade_anterior: l.probabilidade_anterior,
    proximo_passo: l.proximo_passo,
    motivo,
  });
  const comerciais = linhas.filter((l) => l.comercial && l.probabilidade != null);
  const quentes = comerciais
    .filter((l) => (l.probabilidade ?? 0) >= 60)
    .sort((a, b) => (b.probabilidade ?? 0) - (a.probabilidade ?? 0))
    .slice(0, 10)
    .map((l) => base(l, 'Chance alta de fechamento'));
  const em_risco = comerciais
    .filter((l) => (l.probabilidade ?? 0) >= 30)
    .map((l) => {
      const caiu = l.probabilidade_anterior != null && l.probabilidade != null && l.probabilidade_anterior - l.probabilidade >= 20;
      if (l.cliente_sem_resposta) return base(l, 'Cliente ficou sem resposta');
      if (caiu) return base(l, `Chance caiu de ${l.probabilidade_anterior}% para ${l.probabilidade}%`);
      return null;
    })
    .filter((o): o is Oportunidade => !!o)
    .sort((a, b) => (b.probabilidade ?? 0) - (a.probabilidade ?? 0))
    .slice(0, 10);
  return { quentes, em_risco };
}

export interface RelatorioIA {
  nota: number;
  resumo: string;
  pontos_fortes: string[];
  melhorias: Array<{ titulo: string; como_fazer: string; exemplo: string }>;
  insights: string[];
  padroes_objecao: string[];
  recomendacoes_gestao: string[];
}

export const SCHEMA_RELATORIO = {
  type: 'object',
  additionalProperties: false,
  required: ['nota', 'resumo', 'pontos_fortes', 'melhorias', 'insights', 'padroes_objecao', 'recomendacoes_gestao'],
  properties: {
    nota: { type: 'integer' },
    resumo: { type: 'string' },
    pontos_fortes: textos,
    melhorias: {
      type: 'array',
      items: {
        type: 'object', additionalProperties: false, required: ['titulo', 'como_fazer', 'exemplo'],
        properties: { titulo: { type: 'string' }, como_fazer: { type: 'string' }, exemplo: { type: 'string' } },
      },
    },
    insights: textos,
    padroes_objecao: textos,
    recomendacoes_gestao: textos,
  },
} as const;

export function limparRelatorio(bruto: any): RelatorioIA {
  return {
    nota: inteiro(bruto?.nota, 0, 10) ?? 0,
    resumo: texto(bruto?.resumo, 1200),
    pontos_fortes: lista(bruto?.pontos_fortes, 5, 400),
    melhorias: Array.isArray(bruto?.melhorias)
      ? bruto.melhorias
          .slice(0, 5)
          .map((m: any) => ({ titulo: texto(m?.titulo, 120), como_fazer: texto(m?.como_fazer, 400), exemplo: texto(m?.exemplo, 400) }))
          .filter((m: any) => m.titulo)
      : [],
    insights: lista(bruto?.insights, 6, 400),
    padroes_objecao: lista(bruto?.padroes_objecao, 5, 300),
    recomendacoes_gestao: lista(bruto?.recomendacoes_gestao, 4, 400),
  };
}

const SISTEMA_RELATORIO = `Você é um coach de vendas sênior. Recebe as análises de todas as conversas de WhatsApp de UM atendente da Vigor Energy (energia solar por assinatura) em UM dia, mais os números do dia calculados pelo sistema.

Escreva o relatório do dia para o atendente e para a gestão:
- nota: 0 a 10 para a qualidade do atendimento comercial no dia (SPIN, qualificação BANT, objeções, condução para o fechamento, tempo de resposta).
- resumo: 3 a 5 frases sobre o dia (volume, qualidade, o que mais chamou atenção).
- pontos_fortes: até 3, específicos, citando o cliente ou a situação.
- melhorias: até 3, as de maior impacto em vendas. Cada uma com titulo, como_fazer (passo prático) e exemplo (frase pronta que o atendente pode usar).
- insights: padrões úteis (o que os clientes mais perguntam, onde as conversas morrem, que tipo de cliente está mais perto de fechar).
- padroes_objecao: objeções que se repetiram e como responder melhor.
- recomendacoes_gestao: o que a gestão pode fazer (treinamento, material, ajuste de processo). Pode ficar vazia.

Use os números do sistema como estão: não recalcule nem invente números. Se houve alertas de política, mencione no resumo. Não escreva nome de cliente: as conversas vêm numeradas e a lista de clientes já aparece em outro lugar da tela; fale da situação ("o cliente que pediu prazo"), não da pessoa. Português do Brasil, direto, sem frases genéricas.`;

export function pedidoRelatorio(e: {
  dia: string;
  nomeAtendente: string;
  metricas: MetricasDia;
  linhas: LinhaAnalise[];
}): { sistema: string; usuario: string } {
  const m = e.metricas;
  const numeros = [
    `Conversas no dia: ${m.conversas} (${m.conversas_comerciais} de venda).`,
    `Mensagens: ${m.mensagens_recebidas} recebidas, ${m.mensagens_enviadas} enviadas (${m.audios_enviados} áudios enviados).`,
    m.tempo_resposta_mediana_min != null
      ? `Tempo até responder o cliente (horário comercial): mediana ${m.tempo_resposta_mediana_min} min, 90% em até ${m.tempo_resposta_p90_min} min (${m.respostas_medidas} respostas medidas).`
      : 'Tempo de resposta: sem turnos no horário comercial para medir.',
    `Clientes que terminaram o dia sem resposta: ${m.clientes_sem_resposta}.`,
    `Temperatura das vendas: ${m.quentes} quentes, ${m.mornas} mornas, ${m.frias} frias. Probabilidade média: ${m.probabilidade_media ?? 'n/a'}%.`,
    `Alertas de política: ${m.alertas_politica}.`,
  ];
  // Só o essencial de cada conversa: o pedido cresce com o número de conversas.
  //
  // Sem o nome do cliente, de propósito: o texto do relatório fica guardado por
  // dia e não é apagado quando um contato pede exclusão (LGPD). Os nomes e os
  // trechos ficam só nas análises por conversa, que somem junto com o contato.
  const conversas = e.linhas.map((l, i) => {
    const a = l.avaliacao as AnaliseDaConversa['avaliacao'];
    const notas = a?.spin ? `SPIN ${a.spin.nota}, objeções ${a.objecoes.nota}, fechamento ${a.fechamento.nota}` : '';
    return [
      `Conversa ${i + 1} — ${l.comercial ? `${l.probabilidade ?? '?'}% (${l.temperatura ?? '?'}, ${l.etapa ?? '?'})` : 'não comercial'}${l.cliente_sem_resposta ? ' — CLIENTE SEM RESPOSTA' : ''}`,
      `   ${l.resumo ?? ''}`,
      notas ? `   Notas: ${notas}.` : '',
      l.objecoes.length ? `   Objeções: ${l.objecoes.map((o) => `${o.objecao}${o.tratada ? '' : ' (não tratada)'}`).join('; ')}.` : '',
      l.melhorias.length ? `   Melhorar: ${l.melhorias.join(' | ')}` : '',
      l.alertas_politica.length ? `   ALERTA: ${l.alertas_politica.map((x) => `${x.regra} — "${x.trecho}"`).join(' | ')}` : '',
    ].filter(Boolean).join('\n');
  });
  return {
    sistema: SISTEMA_RELATORIO,
    usuario: `Dia: ${e.dia.split('-').reverse().join('/')}. Atendente: ${e.nomeAtendente}.\n\nNúmeros do sistema:\n${numeros.join('\n')}\n\nConversas analisadas:\n${conversas.join('\n')}`,
  };
}
